import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Search, 
  FileUp, 
  FileText, 
  Sparkles, 
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  X,
  Link as LinkIcon,
  UploadCloud,
  FileImage,
  Zap,
  Info,
  Eye,
  Download,
  Loader2,
  ArrowRight,
  CheckCircle2,
  TrendingUp
} from "lucide-react";
import { InvoiceItemRow } from "../InvoiceItemRow";
import { EnhancedInvoiceItemRow } from "@/components/invoice/EnhancedInvoiceItemRow";
import { MatchingDrawer } from "../MatchingDrawer";
import { useWorkstationStore, type InvoiceItem } from "@/hooks/useWorkstationStore";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { SimplifiedFilePreviewModal } from "../invoice/SimplifiedFilePreviewModal";
import { CropAreaData } from "../invoice/CropArea";
import { StatusPill } from "@/components/ui/status-pill";
import { ProcessingProgressDrawer } from "@/components/invoice/ProcessingProgressDrawer";

export const InvoiceMatchingView = () => {
  const {
    invoiceItems,
    products,
    matchProductToInvoice,
    updateInvoiceItem,
    setInvoiceItems,
    orderId,
    isProcessingAI
  } = useWorkstationStore();

  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInvoiceItem, setSelectedInvoiceItem] = useState<string | null>(null);
  const [showMatchingDrawer, setShowMatchingDrawer] = useState(false);
  
  // Real-time document status tracking
  const [docStatus, setDocStatus] = useState<Record<string, any>>({});
  const [progressDrawer, setProgressDrawer] = useState<{
    isOpen: boolean;
    documentId: string;
    documentName: string;
  }>({
    isOpen: false,
    documentId: '',
    documentName: ''
  });
  
  // Feature flag for enhanced invoice rows
  const ENABLE_ENHANCED = import.meta.env.VITE_ENABLE_ENHANCED_INVOICE_COMPARE === 'true';

  // Enhanced row detection and handlers
  const hasEnhancedData = (item: InvoiceItem) =>
    !!(item.size_string || item.pack_qty || item.price_case || item.price_unit || item.pricing_basis);

  const refetchInvoiceItems = async () => {
    if (!orderId) return;
    
    try {
      const { data, error } = await supabase
        .from('invoice_items')
        .select(`
          *,
          invoice_documents!inner(order_id)
        `)
        .eq('invoice_documents.order_id', orderId);

      if (error) throw error;
      setInvoiceItems(data?.map(item => ({
        ...item,
        raw_terms: item.raw_terms || null,
        validation_status: (item.validation_status as any) || 'pending'
      })) || []);
    } catch (error) {
      console.error('Error refetching invoice items:', error);
    }
  };

  const updatePack = async (itemId: string, newPack: number) => {
    const { error } = await supabase
      .from('invoice_items')
      .update({ units_per_case: newPack })
      .eq('id', itemId);
    
    if (!error) {
      refetchInvoiceItems();
      toast({
        title: "Pack size updated",
        description: `Units per case updated to ${newPack}`,
      });
    } else {
      toast({
        title: "Update failed",
        description: "Could not update pack size",
        variant: "destructive"
      });
    }
  };

  const resolveQuantityVariance = async (
    itemId: string,
    action: 'use_invoice' | 'use_received' | 'investigate'
  ) => {
    const { error } = await supabase
      .from('invoice_items')
      .update({ validation_status: action })
      .eq('id', itemId);
    
    if (!error) {
      refetchInvoiceItems();
      toast({
        title: "Variance resolved",
        description: `Action: ${action.replace('_', ' ')}`,
      });
    } else {
      toast({
        title: "Resolution failed",
        description: "Could not resolve quantity variance",
        variant: "destructive"
      });
    }
  };
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    id: string;
    file_name: string;
    file_path: string;
    file_type: 'pdf' | 'image';
    publicUrl: string;
  }>>([]);
  const [submissionStatus, setSubmissionStatus] = useState<'pending' | 'submitted' | 'processing'>('pending');
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{ file: File; filePath: string; type: 'pdf' | 'image' } | null>(null);

  // Real-time subscription for document status updates
  useEffect(() => {
    if (!orderId) return;

    const channel = supabase
      .channel(`invoice-docs-${orderId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'invoice_documents',
        filter: `order_id=eq.${orderId}`
      }, (payload) => {
        console.log('📡 Real-time update:', payload);
        if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
          setDocStatus(prev => ({ ...prev, [(payload.new as any).id]: payload.new }));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId]);

  // Load existing uploaded files for this order
  useEffect(() => {
    const loadUploadedFiles = async () => {
      if (!orderId) return;

      console.log('🔍 Loading uploaded files for order:', orderId);
      
      try {
        const { data, error } = await supabase
          .from('invoice_documents')
          .select('*')
          .eq('order_id', orderId);

        if (error) {
          console.error('❌ Error loading files:', error);
          return;
        }

        console.log('📁 Found files:', data);

        const filesWithUrls = await Promise.all(
          data.map(async (doc) => {
            const { data: urlData } = supabase.storage
              .from('invoice-documents')
              .getPublicUrl(doc.file_path);

            // Initialize docStatus with current document state
            setDocStatus(prev => ({ ...prev, [doc.id]: doc }));

            return {
              id: doc.id,
              file_name: doc.file_name,
              file_path: doc.file_path,
              file_type: doc.file_name.toLowerCase().endsWith('.pdf') ? 'pdf' as const : 'image' as const,
              publicUrl: urlData.publicUrl
            };
          })
        );

        setUploadedFiles(filesWithUrls);
      } catch (error) {
        console.error('❌ Error in loadUploadedFiles:', error);
      }
    };

    loadUploadedFiles();
  }, [orderId]);

  // Filter invoice items
  const filteredInvoiceItems = invoiceItems.filter(item =>
    item.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.barcode_upc && item.barcode_upc.includes(searchQuery))
  );

  // Calculate matching statistics
  const matchedItems = invoiceItems.filter(item => item.matched_product_id);
  const unmatchedItems = invoiceItems.filter(item => !item.matched_product_id);
  const matchingProgress = invoiceItems.length > 0 ? (matchedItems.length / invoiceItems.length) * 100 : 0;

  // Products available for matching (no cost assigned yet)
  const availableProducts = products.filter(p => !p.costPerUnit);

  const handleMatch = (invoiceItemId: string, productId: string) => {
    matchProductToInvoice(productId, invoiceItemId);
    
    // Find the invoice item and calculate cost
    const invoiceItem = invoiceItems.find(item => item.id === invoiceItemId);
    const product = products.find(p => p.id === productId);
    
    if (invoiceItem && product && invoiceItem.total_cost) {
      const totalUnits = product.boxes * product.unitsPerBox;
      const costPerUnit = invoiceItem.total_cost / totalUnits;
      
      // Update product with calculated cost (this would be handled by the backend in real implementation)
      // updateProduct(productId, { costPerUnit });
    }
  };

  const handleShowMatchDrawer = (invoiceItemId: string) => {
    setSelectedInvoiceItem(invoiceItemId);
    setShowMatchingDrawer(true);
  };

  const handleUploadInvoice = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file || !orderId) return;

      setIsUploading(true);
      console.log('📤 Starting file upload:', file.name);

      try {
        // Generate unique file path
        const timestamp = Date.now();
        const extension = file.name.split('.').pop();
        const fileName = `${orderId}_${timestamp}.${extension}`;
        const filePath = `${orderId}/${fileName}`;

        console.log('📁 Uploading to path:', filePath);

        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('invoice-documents')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('❌ Upload error:', uploadError);
          throw uploadError;
        }

        console.log('✅ File uploaded successfully:', uploadData);

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('invoice-documents')
          .getPublicUrl(filePath);

        console.log('🔗 Public URL:', urlData.publicUrl);

        // Save metadata to database
        const { data: dbData, error: dbError } = await supabase
          .from('invoice_documents')
          .insert({
            order_id: orderId,
            file_name: file.name,
            file_path: filePath,
            file_size: file.size,
            processing_status: 'uploaded'
          })
          .select()
          .single();

        if (dbError) {
          console.error('❌ Database error:', dbError);
          throw dbError;
        }

        console.log('💾 Saved to database:', dbData);

        // Update local state
        const newFile = {
          id: dbData.id,
          file_name: file.name,
          file_path: filePath,
          file_type: file.name.toLowerCase().endsWith('.pdf') ? 'pdf' as const : 'image' as const,
          publicUrl: urlData.publicUrl
        };

        setUploadedFiles(prev => [...prev, newFile]);

        toast({
          title: "Upload successful",
          description: `${file.name} has been uploaded successfully.`,
        });

        console.log('🎉 Upload complete!');

      } catch (error) {
        console.error('❌ Upload failed:', error);
        toast({
          title: "Upload failed",
          description: error instanceof Error ? error.message : "Failed to upload file",
          variant: "destructive"
        });
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  };

  const handleFilePreview = async (uploadedFile: typeof uploadedFiles[0]) => {
    console.log('👁️ Opening file preview for:', uploadedFile.file_name);
    
    try {
      // Download the file from Supabase storage
      const { data: fileBlob, error } = await supabase.storage
        .from('invoice-documents')
        .download(uploadedFile.file_path);

      if (error) {
        console.error('❌ Error downloading file:', error);
        toast({
          title: "Preview failed",
          description: "Could not load file for preview",
          variant: "destructive"
        });
        return;
      }

      // Create File object for preview
      const file = new File([fileBlob], uploadedFile.file_name, {
        type: uploadedFile.file_type === 'pdf' ? 'application/pdf' : 'image/*'
      });

      setSelectedFile({
        file,
        filePath: uploadedFile.file_path,
        type: uploadedFile.file_type
      });
      setShowFilePreview(true);

    } catch (error) {
      console.error('❌ Preview error:', error);
      toast({
        title: "Preview failed", 
        description: "Could not open file preview",
        variant: "destructive"
      });
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    const fileToDelete = uploadedFiles.find(f => f.id === fileId);
    if (!fileToDelete) return;

    console.log('🗑️ Deleting file:', fileToDelete.file_name);

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('invoice-documents')
        .remove([fileToDelete.file_path]);

      if (storageError) {
        console.error('❌ Storage delete error:', storageError);
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('invoice_documents')
        .delete()
        .eq('id', fileId);

      if (dbError) {
        console.error('❌ Database delete error:', dbError);
        throw dbError;
      }

      // Update local state
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));

      toast({
        title: "File deleted",
        description: `${fileToDelete.file_name} has been deleted.`,
      });

      console.log('✅ File deleted successfully');

    } catch (error) {
      console.error('❌ Delete failed:', error);
      toast({
        title: "Delete failed",
        description: "Could not delete file",
        variant: "destructive"
      });
    }
  };

  const handleRetryAI = async (fileId?: string) => {
    const filesToProcess = fileId 
      ? uploadedFiles.filter(f => f.id === fileId)
      : uploadedFiles;

    if (filesToProcess.length === 0) {
      toast({
        title: "No files to process",
        description: "Please upload an invoice file first.",
        variant: "destructive"
      });
      return;
    }

    console.log('🔄 Processing uploaded files with enhanced pricing extraction...');
    
    try {
      // Process each uploaded file with enhanced pricing extraction
      for (const file of filesToProcess) {
        console.log('📄 Processing file:', file.file_name);
        
        // Open progress drawer immediately
        setProgressDrawer({
          isOpen: true,
          documentId: file.id,
          documentName: file.file_name
        });
        
        const { data, error } = await supabase.functions.invoke('process-invoice-pdf', {
          body: {
            documentId: file.id,
            filePath: file.file_path,
            options: {
              enhanced_pricing: true,
              use_function_calling: true,
              force_reprocess: true,
              method: 'hybrid'  // Changed from 'text' to 'hybrid'
            }
          }
        });

        if (error) {
          console.error('❌ AI processing error for', file.file_name, ':', error);
          throw new Error(`Processing failed for ${file.file_name}: ${error.message || 'Unknown error'}`);
        }

        console.log('✅ AI processing completed for', file.file_name, ':', data);
        
        // Handle different response statuses
        if (data?.results?.[0]?.status === 'busy') {
          toast({
            title: "Already processing",
            description: "We're now tracking progress for this file.",
            action: (
              <ToastAction
                altText="View progress"
                onClick={() => setProgressDrawer({
                  isOpen: true,
                  documentId: file.id,
                  documentName: file.file_name
                })}
              >
                View progress
              </ToastAction>
            )
          });
          
          // Open progress drawer for busy status
          setProgressDrawer({
            isOpen: true,
            documentId: file.id,
            documentName: file.file_name
          });
        } else {
          // Show enhanced toast with progress tracking
          toast({
            title: "Processing started",
            description: `Tracking ID: ${data?.correlation_id || '...'}`,
            action: (
              <ToastAction
                altText="View progress"
                onClick={() => setProgressDrawer({
                  isOpen: true,
                  documentId: file.id,
                  documentName: file.file_name
                })}
              >
                View progress
              </ToastAction>
            )
          });
        }
      }
      
      // Refresh invoice items after processing
      await refetchInvoiceItems();

    } catch (error) {
      console.error('❌ Enhanced AI processing failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Update document status to failed for each file processed
      for (const processedFile of filesToProcess) {
        try {
          await supabase
            .from('invoice_documents')
            .update({ 
              processing_status: 'failed',
              stage: 'failed',
              stage_message: errorMessage 
            })
            .eq('id', processedFile.id);
        } catch (updateError) {
          console.error('Failed to update document status:', updateError);
        }
      }
      
      toast({
        title: "Enhanced processing failed",
        description: errorMessage.length > 100 ? `${errorMessage.slice(0, 100)}...` : errorMessage,
        variant: "destructive"
      });
      
      // Close progress drawer if open
      setProgressDrawer({ isOpen: false, documentId: '', documentName: '' });
    }
  };

  const handleProcessCrops = async (crops: CropAreaData[], fullPageImages?: File[]) => {
    console.log('🖼️ Processing crops:', crops.length, 'Full pages:', fullPageImages?.length || 0);
    
    try {
      if (crops.length > 0) {
        // Process cropped regions
        console.log('📝 Processing cropped regions...');
        
        // Call AI processing with cropped images
        const { data, error } = await supabase.functions.invoke('process-invoice-pdf', {
          body: {
            orderId,
            crops: crops.map(crop => ({
              blob: crop.blob,
              pageIndex: crop.pageIndex,
              cropArea: crop.cropArea
            }))
          }
        });

        if (error) throw error;

        toast({
          title: "Processing crops",
          description: `Processing ${crops.length} cropped regions...`,
        });
      } else if (fullPageImages && fullPageImages.length > 0) {
        // Process full page images
        console.log('📄 Processing full page images...');
        
        const { data, error } = await supabase.functions.invoke('process-invoice-pdf', {
          body: {
            orderId,
            fullPageImages: fullPageImages
          }
        });

        if (error) throw error;

        toast({
          title: "Processing document",
          description: `Processing ${fullPageImages.length} pages...`,
        });
      }
    } catch (error) {
      console.error('❌ Crop processing failed:', error);
      toast({
        title: "Processing failed",
        description: "Could not process the selected regions",
        variant: "destructive"
      });
    }
  };

  const selectedItem = selectedInvoiceItem 
    ? invoiceItems.find(item => item.id === selectedInvoiceItem) 
    : null;

  // Check if ready for submission
  const isReadyForSubmission = invoiceItems.length > 0 && unmatchedItems.length === 0;

  const handleProceedToSubmit = () => {
    // This would navigate to step 3 - Submit
    console.log("Proceed to submission step");
  };

  const getMatchingReasonPopover = (item: any) => {
    const potentialMatches = availableProducts.filter(product => {
      if (product.barcode === item.barcode_upc) return true;
      const productName = product.name.toLowerCase();
      const itemName = item.product_name.toLowerCase();
      const words = itemName.split(' ');
      return words.some(word => word.length > 2 && productName.includes(word));
    });

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            <Info className="h-3 w-3 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 text-sm">
          <div className="space-y-2">
            <p className="font-medium">AI Matching Notes:</p>
            <ul className="space-y-1">
              {item.barcode_upc ? (
                <li className="flex items-center gap-2">
                  {potentialMatches.some(p => p.barcode === item.barcode_upc) ? (
                    <CheckCircle className="h-3 w-3 text-success" />
                  ) : (
                    <X className="h-3 w-3 text-destructive" />
                  )}
                  <span>Barcode: {item.barcode_upc}</span>
                </li>
              ) : (
                <li className="flex items-center gap-2">
                  <X className="h-3 w-3 text-destructive" />
                  <span>No barcode found</span>
                </li>
              )}
              <li className="flex items-center gap-2">
                {potentialMatches.length > 0 ? (
                  <CheckCircle className="h-3 w-3 text-success" />
                ) : (
                  <X className="h-3 w-3 text-destructive" />
                )}
                <span>Name match: {potentialMatches.length} similar products</span>
              </li>
            </ul>
            {potentialMatches.length === 0 && (
              <p className="text-muted-foreground text-xs mt-2">
                No matching products found. Try manual matching.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div className="space-y-6">
      {/* AI Processing Status Banner */}
      {isProcessingAI && (
        <Alert className="border-warning/20 bg-warning/10">
          <Loader2 className="h-4 w-4 animate-spin" />
          <div className="ml-2">
            <div className="font-medium">AI is currently processing your invoice file</div>
            <div className="text-sm text-muted-foreground">
              Please wait while we extract and match invoice items...
              {uploadedFiles.length > 0 && ` (${uploadedFiles.length} files)`}
            </div>
          </div>
        </Alert>
      )}

      {/* Submission Status */}
      {submissionStatus === 'submitted' && (
        <Alert className="border-success/20 bg-success/10">
          <CheckCircle2 className="h-4 w-4" />
          <div className="ml-2">
            <div className="font-medium">✅ Already Submitted</div>
            <div className="text-sm text-muted-foreground">
              This invoice has been processed and submitted to the system.
            </div>
          </div>
        </Alert>
      )}

      {/* Ready for Submission Banner */}
      {isReadyForSubmission && submissionStatus === 'pending' && (
        <Alert className="border-success/20 bg-success/10">
          <CheckCircle2 className="h-4 w-4" />
          <div className="ml-2 flex-1">
            <div className="font-medium">All items matched!</div>
            <div className="text-sm text-muted-foreground">
              You can now proceed to <strong>Step 3: Submission Console</strong>.
            </div>
          </div>
          <Button size="sm" className="ml-4" onClick={handleProceedToSubmit}>
            <ArrowRight className="h-4 w-4 mr-1" />
            Go to Submit
          </Button>
        </Alert>
      )}

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* Header with Upload and Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search invoice items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant={uploadedFiles.length > 0 ? "secondary" : "default"} 
            onClick={handleUploadInvoice}
            disabled={isUploading}
            className="flex items-center gap-2 relative group"
          >
            {isUploading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : uploadedFiles.length > 0 ? (
              <CheckCircle className="w-4 h-4 text-success" />
            ) : (
              <FileUp className="w-4 h-4" />
            )}
            {isUploading ? "Uploading..." : uploadedFiles.length > 0 ? "Add More" : "Upload Invoice"}
            {uploadedFiles.length > 0 && (
              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-popover text-popover-foreground text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded
              </span>
            )}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={() => handleRetryAI()}
            disabled={isProcessingAI || uploadedFiles.some(f => docStatus[f.id]?.processing_status === 'processing' || docStatus[f.id]?.stage === 'busy')}
            className="flex items-center gap-2 hover:bg-primary/5 hover:border-primary/30 min-h-[44px]"
          >
            <Sparkles className={`w-4 h-4 ${isProcessingAI ? 'animate-pulse text-primary' : ''}`} />
            Retry AI
          </Button>
        </div>
      </div>

          {/* Stats and Progress */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Items</p>
                <p className="text-2xl font-bold">{invoiceItems.length}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Matched</p>
                <p className="text-2xl font-bold text-green-600">{matchedItems.length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Unmatched</p>
                <p className="text-2xl font-bold text-orange-600">{unmatchedItems.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Available</p>
                <p className="text-2xl font-bold text-blue-600">{availableProducts.length}</p>
              </div>
              <LinkIcon className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

          {/* Matching Progress */}
          {invoiceItems.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Matching Progress</span>
              <span className="text-sm text-gray-500">{matchingProgress.toFixed(0)}%</span>
            </div>
            <Progress value={matchingProgress} className="w-full" />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{matchedItems.length} matched</span>
              <span>{unmatchedItems.length} remaining</span>
            </div>
          </CardContent>
        </Card>
      )}

          {/* Invoice Items List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Unmatched Items */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Unmatched Items ({unmatchedItems.length})
                </CardTitle>
              </CardHeader>
          <CardContent>
            <ScrollArea className="h-96">
              <div className="space-y-3">
                    {unmatchedItems.filter(item =>
                      item.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (item.barcode_upc && item.barcode_upc.includes(searchQuery))
                    ).map((item) => {
                      // Find potential matches for this item
                      const potentialMatches = availableProducts.filter(product => {
                        // Exact barcode match
                        if (product.barcode === item.barcode_upc) return true;
                        
                        // Name similarity (basic)
                        const productName = product.name.toLowerCase();
                        const itemName = item.product_name.toLowerCase();
                        const words = itemName.split(' ');
                        
                        return words.some(word => word.length > 2 && productName.includes(word));
                      });

                       const useEnhanced = ENABLE_ENHANCED && hasEnhancedData(item);
                       const matchedProduct = products.find(p => p.id === item.matched_product_id);
                       const enhancedProduct = matchedProduct ? {
                         ...matchedProduct,
                         units_per_box: matchedProduct.unitsPerBox
                       } : undefined;
                       
                       return (
                         <div key={item.id} className="relative">
                           {useEnhanced ? (
                             <EnhancedInvoiceItemRow
                               invoiceItem={item}
                               matchedProduct={enhancedProduct}
                               onUpdatePack={updatePack}
                               onResolveQuantityVariance={resolveQuantityVariance}
                             />
                           ) : (
                             <InvoiceItemRow
                               invoiceItem={item}
                               matchingProducts={potentialMatches}
                               onEdit={updateInvoiceItem}
                               onMatch={handleMatch}
                               onShowMatchDrawer={handleShowMatchDrawer}
                               variant="detailed"
                             />
                           )}
                           <div className="absolute top-2 right-2">
                             {getMatchingReasonPopover(item)}
                           </div>
                         </div>
                       );
                    })}
                    
                    {unmatchedItems.length === 0 && invoiceItems.length > 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-success/60" />
                        <div className="text-lg font-medium text-foreground">All items matched!</div>
                        <div className="text-sm">Great job on the matching process.</div>
                      </div>
                    )}
                    
                    {invoiceItems.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <FileImage className="w-16 h-16 mx-auto mb-4 opacity-40" />
                        <div className="text-lg font-medium text-foreground">No invoice items yet</div>
                        <div className="text-sm mb-4">Upload an invoice to start matching products</div>
                        <Button 
                          variant="outline" 
                          onClick={handleUploadInvoice}
                          className="flex items-center gap-2"
                        >
                          <UploadCloud className="w-4 h-4" />
                          Upload Invoice
                        </Button>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Matched Items */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Matched Items ({matchedItems.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-96">
                  <div className="space-y-3">
                    {matchedItems.filter(item =>
                      item.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (item.barcode_upc && item.barcode_upc.includes(searchQuery))
                      ).map((item) => {
                       const useEnhanced = ENABLE_ENHANCED && hasEnhancedData(item);
                       const matchedProduct = products.find(p => p.id === item.matched_product_id);
                       const enhancedProduct = matchedProduct ? {
                         ...matchedProduct,
                         units_per_box: matchedProduct.unitsPerBox
                       } : undefined;
                       
                       return useEnhanced ? (
                         <EnhancedInvoiceItemRow
                           key={item.id}
                           invoiceItem={item}
                           matchedProduct={enhancedProduct}
                           onUpdatePack={updatePack}
                           onResolveQuantityVariance={resolveQuantityVariance}
                         />
                       ) : (
                         <InvoiceItemRow
                           key={item.id}
                           invoiceItem={item}
                           onEdit={updateInvoiceItem}
                           variant="compact"
                         />
                       );
                     })}
                    
                    {matchedItems.length === 0 && invoiceItems.length > 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <LinkIcon className="w-16 h-16 mx-auto mb-4 opacity-40" />
                        <div className="text-lg font-medium text-foreground">No items matched yet</div>
                        <div className="text-sm">Start matching items from the unmatched panel.</div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Upload Status Summary Panel (Right Side) */}
        <div className="w-80 space-y-4">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="text-lg">Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Items:</span>
                  <span className="font-medium">{invoiceItems.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Matched:</span>
                  <span className="font-medium text-success">{matchedItems.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Unmatched:</span>
                  <span className="font-medium text-warning">{unmatchedItems.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>AI Status:</span>
                  <Badge variant={isProcessingAI ? "secondary" : "outline"}>
                    {isProcessingAI ? "Processing" : "Complete"}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Files:</span>
                  <span className="font-medium">{uploadedFiles.length}</span>
                </div>
              </div>

              {uploadedFiles.length > 0 && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="text-sm font-medium">Uploaded Files:</div>
                  <ScrollArea className="max-h-48">
                    <div className="space-y-2">
                      {uploadedFiles.map((file) => {
                        const liveStatus = docStatus[file.id] || {};
                        return (
                          <div key={file.id} className="flex items-center gap-2 p-3 border rounded-lg">
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="text-xs font-medium truncate">
                                {file.file_name}
                              </div>
                              <div className="flex items-center gap-2">
                                <StatusPill 
                                  status={liveStatus.processing_status}
                                  stage={liveStatus.stage}
                                  message={liveStatus.stage_message}
                                />
                                <span className="text-xs text-muted-foreground">
                                  {file.file_type.toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => setProgressDrawer({
                                  isOpen: true,
                                  documentId: file.id,
                                  documentName: file.file_name
                                })}
                                title="View Progress"
                              >
                                <TrendingUp className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => handleFilePreview(file)}
                                title="Preview"
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-6 w-6 p-0 min-h-[44px] min-w-[44px] md:min-h-auto md:min-w-auto"
                                onClick={() => handleRetryAI(file.id)}
                                disabled={liveStatus.processing_status === 'processing' || liveStatus.stage === 'busy'}
                                title="Retry AI"
                              >
                                <Sparkles className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteFile(file.id)}
                                title="Delete"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* File Preview Modal */}
      {selectedFile && (
        <SimplifiedFilePreviewModal
          isOpen={showFilePreview}
          onClose={() => {
            setShowFilePreview(false);
            setSelectedFile(null);
          }}
          filePath={selectedFile.filePath}
          fileType={selectedFile.type}
          onCropComplete={() => {
            // Handle crop completion if needed
            console.log('Crop completed');
          }}
          onDelete={() => {
            if (selectedFile) {
              const fileToDelete = uploadedFiles.find(f => f.file_path === selectedFile.filePath);
              if (fileToDelete) {
                handleDeleteFile(fileToDelete.id);
              }
            }
            setShowFilePreview(false);
            setSelectedFile(null);
          }}
          onProcessCrops={handleProcessCrops}
        />
      )}

      {/* Progress Drawer */}
      <ProcessingProgressDrawer
        isOpen={progressDrawer.isOpen}
        onClose={() => setProgressDrawer(prev => ({ ...prev, isOpen: false }))}
        documentId={progressDrawer.documentId}
        documentName={progressDrawer.documentName}
      />

      {/* Matching Drawer */}
      <MatchingDrawer
        isOpen={showMatchingDrawer}
        onClose={() => {
          setShowMatchingDrawer(false);
          setSelectedInvoiceItem(null);
        }}
        invoiceItem={selectedItem}
        availableProducts={availableProducts}
        onMatch={handleMatch}
      />
    </div>
  );
};